import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { returnOrderService } from "services";
import {
  ReturnOrderForm,
  ReturnOrderQuery,
  ReturnOrderUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type ReturnOrderListKey = ["return-orders"] | ["return-orders", ReturnOrderQuery];
type ReturnOrderDetailKey = ["return-orders", "detail", number | string];

const returnOrderKeys = {
  all: ["return-orders"] as const,
  list: (params?: ReturnOrderQuery): ReturnOrderListKey =>
    params ? ["return-orders", params] : ["return-orders"],
  detail: (id: number | string): ReturnOrderDetailKey => [
    "return-orders",
    "detail",
    id,
  ],
};

export function useReturnOrder() {
  const queryClient = useQueryClient();

  const fetchList = (params?: ReturnOrderQuery, enabled = true) => {
    return useQuery({
      queryKey: returnOrderKeys.list(params),
      queryFn: () => returnOrderService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: data?.totalPage ?? 1,
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: returnOrderKeys.detail(id),
      queryFn: () => returnOrderService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createReturnOrder = useMutation({
    mutationFn: (payload: ReturnOrderForm) => returnOrderService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(res?.message ?? "Tạo chứng từ nhập lại thành công");
      }
      queryClient.invalidateQueries({ queryKey: returnOrderKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo chứng từ nhập lại thất bại");
      console.error("createReturnOrder failed", err);
    },
  });

  const updateReturnOrder = useMutation({
    mutationFn: (payload: ReturnOrderUpdatePayload) =>
      returnOrderService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Cập nhật chứng từ nhập lại thành công");
      }
      queryClient.invalidateQueries({ queryKey: returnOrderKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật chứng từ nhập lại thất bại");
      console.error("updateReturnOrder failed", err);
    },
  });

  const deleteReturnOrder = useMutation({
    mutationFn: (id: number | string) => returnOrderService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Xoá chứng từ nhập lại thành công");
      }
      queryClient.invalidateQueries({ queryKey: returnOrderKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá chứng từ nhập lại thất bại");
      console.error("deleteReturnOrder failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createReturnOrder,
    updateReturnOrder,
    deleteReturnOrder,
  };
}
